import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import premiumBanner from "../assets/zuno/zuno_premium_conforto_em_movimento.png";
import monthlyPlanBanner from "../assets/zuno/plano_mensalidade_100_ao_motorista.png";
import flexPlanBanner from "../assets/zuno/banner_neon_futurista_do_plano_flex.png";
import premiumLightBanner from "../assets/zuno/premium-light.png";
import flexLightBanner from "../assets/zuno/plan-flex-light.png";
import proLightBanner from "../assets/zuno/plan-pro-light.png";
import proDarkBanner from "../assets/zuno/plan-pro-dark.png";
import { useAccessibility } from "../accessibility/AccessibilityContext";
import { markPerformance } from "../services/performanceTrace";

export const CAROUSEL_AUTOPLAY_MS = 3000;

export const HOME_CAMPAIGNS = [
  { id: "zuno-premium", title: "Zuno Premium", route: "/zuno-premium", image: premiumBanner, alt: "Zuno Premium: mais conforto, mais segurança, mais você" },
  { id: "plano-mensalidade", title: "Plano Mensalidade", route: "/plano-mensalidade", image: monthlyPlanBanner, alt: "Plano Mensalidade Zuno: 100% da corrida para o motorista" },
  { id: "plano-flex", title: "Plano Flex", route: "/plano-flex", image: flexPlanBanner, alt: "Plano Flex Zuno: somente 8% de comissão por corrida" },
];

const IOS_HOME_CAMPAIGNS = {
  light: [
    { ...HOME_CAMPAIGNS[0], image: premiumLightBanner, containArtwork: true },
    { ...HOME_CAMPAIGNS[2], image: flexLightBanner, containArtwork: true },
    { ...HOME_CAMPAIGNS[1], id: "plano-pro", title: "Plano Pro", image: proLightBanner, alt: "Plano Pro: 100% da corrida para o motorista no plano mensal", containArtwork: true },
  ],
  dark: [
    HOME_CAMPAIGNS[0], HOME_CAMPAIGNS[2],
    { ...HOME_CAMPAIGNS[1], id: "plano-pro", title: "Plano Pro", image: proDarkBanner, alt: "Plano Pro: 100% da corrida para o motorista no plano mensal", containArtwork: true },
  ],
};

export const getIOSHomeCampaigns = theme => IOS_HOME_CAMPAIGNS[theme === "light" ? "light" : "dark"];

const preloadedCampaigns = new Set();

export function preloadHomeCampaignImages(campaigns = HOME_CAMPAIGNS) {
  if (typeof window === "undefined" || typeof window.Image !== "function") return;
  campaigns.forEach(({ image }) => {
    if (!image || preloadedCampaigns.has(image)) return;
    preloadedCampaigns.add(image);
    const preload = new window.Image();
    preload.decoding = "async";
    preload.src = image;
  });
}

export default function HomePromoCarousel({ campaigns = HOME_CAMPAIGNS }) {
  const navigate = useNavigate();
  const { reduceMotion } = useAccessibility();
  const [systemReduceMotion, setSystemReduceMotion] = useState(() => window.matchMedia?.("(prefers-reduced-motion: reduce)").matches === true);
  const physicalIndexRef = useRef(0);
  const pointerStartRef = useRef(null);
  const draggedRef = useRef(false);
  const resetFramesRef = useRef([]);
  const [physicalIndex, setPhysicalIndex] = useState(0);
  const [transitionEnabled, setTransitionEnabled] = useState(true);
  const [isPaused, setIsPaused] = useState(false);
  const [isDocumentVisible, setIsDocumentVisible] = useState(() => typeof document === "undefined" || document.visibilityState !== "hidden");
  const logicalIndex = campaigns.length ? physicalIndex % campaigns.length : 0;
  const autoplayDisabled = reduceMotion || systemReduceMotion;

  useEffect(() => {
    markPerformance("BANNERS_START", { count: campaigns.length }, { onlyOnce: true });
    const schedule = window.requestIdleCallback || ((callback) => window.setTimeout(callback, 1200));
    const cancel = window.cancelIdleCallback || window.clearTimeout;
    const id = schedule(() => {
      preloadHomeCampaignImages(campaigns.slice(1));
      markPerformance("BANNERS_DONE", { deferred: true }, { onlyOnce: true });
    });
    return () => cancel(id);
  }, [campaigns]);

  useEffect(() => {
    const query = window.matchMedia?.("(prefers-reduced-motion: reduce)");
    if (!query) return undefined;
    const update = event => setSystemReduceMotion(event.matches);
    query.addEventListener?.("change", update);
    return () => query.removeEventListener?.("change", update);
  }, []);

  const moveTo = useCallback((index, animated = true) => {
    physicalIndexRef.current = index;
    setTransitionEnabled(animated);
    setPhysicalIndex(index);
  }, []);

  useEffect(() => {
    const handleVisibility = () => {
      const visible = document.visibilityState !== "hidden";
      setIsDocumentVisible(visible);
      if (visible && physicalIndexRef.current >= campaigns.length) moveTo(0, false);
    };
    document.addEventListener("visibilitychange", handleVisibility);
    return () => document.removeEventListener("visibilitychange", handleVisibility);
  }, [campaigns.length, moveTo]);

  const resetCloneWithoutAnimation = useCallback(() => {
    if (!campaigns.length || physicalIndexRef.current !== campaigns.length) return;
    setTransitionEnabled(false);
    physicalIndexRef.current = 0;
    setPhysicalIndex(0);
    const firstFrame = requestAnimationFrame(() => {
      const secondFrame = requestAnimationFrame(() => setTransitionEnabled(true));
      resetFramesRef.current.push(secondFrame);
    });
    resetFramesRef.current.push(firstFrame);
  }, [campaigns.length]);

  useEffect(() => {
    if (campaigns.length < 2 || isPaused || autoplayDisabled || !isDocumentVisible) return undefined;
    const intervalId = window.setInterval(() => {
      const current = physicalIndexRef.current;
      moveTo(current >= campaigns.length ? 0 : current + 1, current < campaigns.length);
    }, CAROUSEL_AUTOPLAY_MS);
    return () => window.clearInterval(intervalId);
  }, [autoplayDisabled, campaigns.length, isDocumentVisible, isPaused, moveTo]);

  useEffect(() => () => resetFramesRef.current.forEach(cancelAnimationFrame), []);

  if (!campaigns.length) return null;
  const slides = campaigns.length > 1 ? [...campaigns, { ...campaigns[0], id: `${campaigns[0].id}-loop-clone`, clone: true }] : campaigns;

  const finishPointer = (clientX) => {
    if (pointerStartRef.current === null) return;
    const delta = clientX - pointerStartRef.current;
    draggedRef.current = Math.abs(delta) > 8;
    if (Math.abs(delta) > 42) {
      if (delta < 0) moveTo(physicalIndexRef.current >= campaigns.length ? 1 : physicalIndexRef.current + 1);
      else moveTo((logicalIndex - 1 + campaigns.length) % campaigns.length);
    }
    pointerStartRef.current = null;
    setIsPaused(false);
  };

  return (
    <section className="zuno-home-campaigns" data-autoplay={autoplayDisabled ? "off" : "on"} aria-roledescription="carrossel" aria-label="Campanhas e benefícios Zuno">
      <div
        className="zuno-home-campaign-track"
        onPointerDown={(event) => { pointerStartRef.current = event.clientX; draggedRef.current = false; setIsPaused(true); }}
        onPointerUp={(event) => finishPointer(event.clientX)}
        onPointerCancel={() => { pointerStartRef.current = null; draggedRef.current = false; setIsPaused(false); }}
      >
        <div
          className="zuno-home-campaign-slider"
          data-animated={transitionEnabled ? "true" : "false"}
          style={{ transform: `translate3d(-${physicalIndex * 100}%,0,0)` }}
          onTransitionEnd={resetCloneWithoutAnimation}
        >
          {slides.map((campaign, index) => (
            <button
              className="zuno-home-campaign"
              data-artwork-fit={campaign.containArtwork ? "contain" : undefined}
              key={campaign.id}
              type="button"
              tabIndex={campaign.clone ? -1 : 0}
              aria-hidden={campaign.clone ? "true" : undefined}
              onClick={(event) => {
                if (draggedRef.current) { event.preventDefault(); draggedRef.current = false; return; }
                navigate(campaign.route);
              }}
              aria-label={campaign.clone ? undefined : `Abrir ${campaign.title}`}
              aria-current={!campaign.clone && index === logicalIndex ? "true" : undefined}
            >
              <img
                src={campaign.image}
                alt={campaign.clone ? "" : campaign.alt}
                draggable="false"
                loading={index === 0 ? "eager" : "lazy"}
                decoding="async"
                fetchpriority={index === 0 ? "high" : "auto"}
              />
            </button>
          ))}
        </div>
      </div>

      <div className="zuno-home-campaign-dots" aria-label={`Campanha ${logicalIndex + 1} de ${campaigns.length}`}>
        {campaigns.map((campaign, index) => (
          <button key={campaign.id} type="button" data-active={index === logicalIndex} onClick={() => moveTo(index)} aria-label={`Mostrar ${campaign.title}`} aria-current={index === logicalIndex ? "true" : undefined} />
        ))}
      </div>
    </section>
  );
}
